import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../common/Icon/Icon';
import AvatarFrame from '../../common/AvatarFrame/AvatarFrame';
import ConfirmDialog from '../../common/ConfirmDialog/ConfirmDialog';
import { getProfilePhotoSrc, PLACEHOLDER_DATA_URI } from '../../../utils/profilePhoto';
import { adminFetch, ago, shortDate, dateTime, StatusChip } from './adminUtil';

/**
 * Look up anyone or any trade from one box, and fix it on the spot:
 * a person's whole record (trades, reports, blocks, chat) with the
 * account actions beside it, or a trade's whole story (cards, packages,
 * every event) with a staff cancel for the ones that can't finish.
 */

function Avatar({ user, size = 40 }) {
    return (
        <AvatarFrame user={user} className="adm-avatar">
            <img
                src={getProfilePhotoSrc(user)}
                alt=""
                width={size}
                height={size}
                onError={e => { e.target.onerror = null; e.target.src = PLACEHOLDER_DATA_URI; }}
            />
        </AvatarFrame>
    );
}

// ---------------- One person ----------------

function UserDetail({ id, go, onChanged }) {
    const [d, setD] = useState(null);
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    const [confirm, setConfirm] = useState(null);   // 'ban' | 'signout' | 'shop'
    const [banReason, setBanReason] = useState('');

    const load = useCallback(async () => {
        try { setD(await adminFetch(`/api/admin/users/${id}`)); setErr(null); }
        catch (e) { setErr(e.message); }
    }, [id]);
    useEffect(() => { setD(null); load(); }, [load]);

    const act = async (url, body, done) => {
        setBusy(true); setMsg(null);
        try {
            await adminFetch(url, { method: 'POST', body: body || {} });
            setMsg(done);
            await load();
            onChanged();
        } catch (e) { setMsg(e.message); }
        finally { setBusy(false); setConfirm(null); }
    };

    if (err) return <div className="adm-banner bad">{err}</div>;
    if (!d) return <p className="adm-loading">Loading account&hellip;</p>;
    const { user: u } = d;
    const total = Object.values(d.tradeCounts).reduce((a, b) => a + b, 0);
    const openReports = d.reports.filter(r => r.status === 'open').length;

    const confirmCopy = {
        ban: {
            title: `Ban ${u.username}?`, label: 'Ban account', danger: true,
            message: (
                <div className="adm-confirm-body">
                    <p>They're signed out, can't log in, and disappear from search, matches and trades. Nothing is deleted and you can unban later. Their open reports resolve.</p>
                    <textarea rows={2} maxLength={255} placeholder="Reason (kept on record, optional)"
                        value={banReason} onChange={e => setBanReason(e.target.value)} />
                </div>
            ),
            run: () => act(`/api/admin/users/${u.id}/ban`, { reason: banReason.trim() }, `${u.username} is banned.`),
        },
        signout: {
            title: `Sign ${u.username} out everywhere?`, label: 'Sign out',
            message: 'Every device they are logged in on is logged out. Use it for a shared or stolen login.',
            run: () => act(`/api/admin/users/${u.id}/sign-out`, null, `${u.username} is signed out everywhere.`),
        },
        shop: {
            title: `Make ${u.username} a shop?`, label: 'Make shop',
            message: 'They get a shop (waiting for its first payment) and the shop dashboard. Record a payment under Shops to switch it on.',
            run: () => act('/api/shops/admin/grant', { userId: u.id }, `${u.username} now has a shop.`),
        },
    }[confirm];

    return (
        <article className="adm-record">
            <ConfirmDialog
                open={!!confirm}
                title={confirmCopy?.title}
                message={confirmCopy?.message}
                confirmLabel={confirmCopy?.label}
                danger={!!confirmCopy?.danger}
                busy={busy}
                onCancel={() => setConfirm(null)}
                onConfirm={() => confirmCopy.run()}
            />

            <header className="adm-record-head">
                <Avatar user={u} size={56} />
                <div className="adm-record-title">
                    <h2>{u.username} <span className="adm-muted">#{u.id}</span></h2>
                    <div className="adm-chips">
                        {u.role !== 'user' && <span className={`adm-chip role-${u.role}`}>{u.role}</span>}
                        {u.is_banned && <span className="adm-chip bad">banned</span>}
                        {u.is_deleted && <span className="adm-chip">deleted</span>}
                        <span className={`adm-chip${u.is_email_verified ? ' ok' : ''}`}>
                            {u.is_email_verified ? 'email verified' : 'email not verified'}
                        </span>
                        {openReports > 0 && <span className="adm-chip warn">{openReports} open report{openReports === 1 ? '' : 's'}</span>}
                    </div>
                </div>
            </header>

            <dl className="adm-facts">
                <div><dt>Email</dt><dd>{u.email}</dd></div>
                <div><dt>Joined</dt><dd>{shortDate(u.created_at)}</dd></div>
                <div><dt>Last active</dt><dd>{d.lastActive ? ago(d.lastActive) : 'no activity yet'}</dd></div>
                <div><dt>Location</dt><dd>{u.location || 'not set'}</dd></div>
                <div><dt>Binder</dt><dd>{d.binder.haves} cards, {d.binder.wants} wanted</dd></div>
                <div><dt>Chat</dt><dd>{d.chat.chats} chats, {d.chat.sent} sent</dd></div>
                <div><dt>Blocks</dt><dd>blocks {d.blocks.blocking}, blocked by {d.blocks.blockedBy}</dd></div>
                <div><dt>Experience</dt><dd>{u.experience ?? 0} XP</dd></div>
                {d.shop && (
                    <div><dt>Shop</dt><dd>{d.shop.name} ({d.shop.status.replace('_', ' ')})</dd></div>
                )}
                {u.is_banned && u.banned_reason && (
                    <div><dt>Ban reason</dt><dd>{u.banned_reason}</dd></div>
                )}
            </dl>

            <div className="adm-actions">
                <Link className="adm-btn" to={`/profile/${u.id}`}>View profile</Link>
                <Link className="adm-btn" to={`/messages?to=${u.id}`}><Icon name="chat" size={13} /> Message</Link>
                {!u.is_email_verified && (
                    <button type="button" className="adm-btn" disabled={busy}
                        onClick={() => act(`/api/admin/users/${u.id}/verify-email`, null, 'Email marked verified.')}>
                        <Icon name="check" size={13} /> Mark email verified
                    </button>
                )}
                {u.role === 'user' && !d.shop && (
                    <button type="button" className="adm-btn" disabled={busy} onClick={() => setConfirm('shop')}>
                        <Icon name="store" size={13} /> Make shop
                    </button>
                )}
                <button type="button" className="adm-btn" disabled={busy} onClick={() => setConfirm('signout')}>
                    Sign out everywhere
                </button>
                {u.role !== 'admin' && (u.is_banned ? (
                    <button type="button" className="adm-btn danger" disabled={busy}
                        onClick={() => act(`/api/admin/users/${u.id}/unban`, null, `${u.username} is unbanned.`)}>
                        Unban
                    </button>
                ) : (
                    <button type="button" className="adm-btn danger" disabled={busy}
                        onClick={() => { setBanReason(''); setConfirm('ban'); }}>
                        <Icon name="block" size={13} /> Ban
                    </button>
                ))}
            </div>
            {msg && <p className="adm-msg">{msg}</p>}

            <section className="adm-block">
                <h3 className="adm-h3">Trades <span>{total}</span></h3>
                {d.trades.length === 0 ? <p className="adm-muted">No trades yet.</p> : (
                    <ul className="adm-rows">
                        {d.trades.map(t => (
                            <li key={t.tradeId}>
                                <button type="button" className="adm-row" onClick={() => go('lookup', { trade: t.trade_code || t.tradeId })}>
                                    <span className="adm-mono">{t.trade_code || `#${t.tradeId}`}</span>
                                    <span className="adm-row-main">with {t.partner || 'nobody yet'}</span>
                                    <StatusChip status={t.status} />
                                    <span className="adm-row-date">{shortDate(t.created_at)}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section className="adm-block">
                <h3 className="adm-h3">Reports about them <span>{d.reports.length}</span></h3>
                {d.reports.length === 0 ? <p className="adm-muted">Nobody has reported them. They have filed {d.reportsFiled}.</p> : (
                    <ul className="adm-filings">
                        {d.reports.map(r => (
                            <li key={r.id} className={r.status !== 'open' ? 'settled' : ''}>
                                <span><strong>{r.reporter}</strong> · {r.reason} · {shortDate(r.created_at)}{r.status !== 'open' && ` · ${r.status}`}</span>
                                {r.detail && <p>{r.detail}</p>}
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </article>
    );
}

// ---------------- One trade ----------------

// The stops a package makes, in order, with the column that dates each
function legStops(l) {
    if (l.method !== 'shop') {
        return [['Shipped', l.shipped_at], ['Delivered', l.delivered_at]];
    }
    const pickup = l.dest_type === 'shop';
    const elsewhere = pickup && l.dest_shop_id && l.dest_shop_id !== l.shop_id;
    const stops = [['Shipped', l.shipped_at], ['At shop', l.shop_received_at], ['Checked', l.shop_verified_at]];
    if (elsewhere) stops.push(['Sent on', l.forwarded_at]);
    if (pickup) stops.push(['Ready for pickup', l.at_pickup_at], ['Picked up', l.delivered_at]);
    else stops.push(['Delivered', l.delivered_at]);
    return stops;
}

function LegTrack({ leg, names }) {
    const stops = legStops(leg);
    const rejected = leg.status === 'shop_rejected';
    return (
        <div className={`adm-leg${rejected ? ' rejected' : ''}`}>
            <div className="adm-leg-head">
                <strong>{names[leg.sender_id]}</strong>
                <Icon name="arrow-right" size={13} />
                <strong>{names[leg.receiver_id]}</strong>
                <span className="adm-muted">
                    {leg.method === 'shop' ? `via ${leg.shop_name || 'a shop'}` : 'mailed directly'}
                    {leg.dest_type === 'shop' ? `, pickup at ${leg.dest_shop_name || 'their shop'}` : leg.method === 'shop' ? ', then mailed' : ''}
                </span>
                {leg.shop_status === 'suspended' && <span className="adm-chip bad">shop suspended</span>}
            </div>
            <ol className="adm-stops">
                {stops.map(([label, at], i) => {
                    const done = !!at;
                    const isNext = !done && (i === 0 || !!stops[i - 1][1]) && !rejected;
                    return (
                        <li key={label} className={done ? 'done' : isNext ? 'next' : ''}>
                            <span className="adm-stop-dot" />
                            <span className="adm-stop-label">{label}</span>
                            <span className="adm-stop-date">{done ? shortDate(at) : isNext ? 'waiting' : ''}</span>
                        </li>
                    );
                })}
                {rejected && (
                    <li className="rejected">
                        <span className="adm-stop-dot" />
                        <span className="adm-stop-label">Rejected</span>
                        <span className="adm-stop-date">{leg.shop_rejected_at ? shortDate(leg.shop_rejected_at) : ''}</span>
                    </li>
                )}
            </ol>
        </div>
    );
}

function CardList({ title, cards, sub }) {
    return (
        <div className="adm-cards">
            <h4>{title} {sub != null && <span className="adm-muted">${sub.toFixed(2)} added</span>}</h4>
            {cards.length === 0 ? <p className="adm-muted">No cards.</p> : (
                <ul>
                    {cards.map((c, i) => (
                        <li key={i}>
                            {c.image ? <img src={c.image} alt="" loading="lazy" /> : <span className="adm-card-ph" />}
                            <span className="adm-card-name">
                                {c.quantity > 1 && `${c.quantity}x `}{c.name}
                                <small>{c.setCode}{c.finish && c.finish !== 'nonfoil' ? ` · ${c.finish}` : ''}</small>
                            </span>
                            {c.price != null && <span className="adm-card-price">${c.price.toFixed(2)}</span>}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function TradeDetail({ tradeRef, go, onChanged }) {
    const [d, setD] = useState(null);
    const [err, setErr] = useState(null);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);

    const load = useCallback(async () => {
        try { setD(await adminFetch(`/api/admin/trades/${encodeURIComponent(tradeRef)}`)); setErr(null); }
        catch (e) { setErr(e.message); }
    }, [tradeRef]);
    useEffect(() => { setD(null); load(); }, [load]);

    const cancel = async () => {
        setBusy(true); setMsg(null);
        try {
            await adminFetch(`/api/admin/trades/${d.trade.tradeId}/cancel`, { method: 'POST', body: { reason: reason.trim() } });
            setMsg('Trade cancelled. Both traders were notified.');
            await load();
            onChanged();
        } catch (e) { setMsg(e.message); }
        finally { setBusy(false); setCancelOpen(false); }
    };

    if (err) return <div className="adm-banner bad">{err}</div>;
    if (!d) return <p className="adm-loading">Loading trade&hellip;</p>;
    const { trade: t, legs, events } = d;
    const names = { [t.owner?.id]: t.owner?.username, [t.other?.id]: t.other?.username };
    const lastEvent = events.length ? events[events.length - 1].created_at : t.created_at;
    const open = !['finished', 'cancelled'].includes(t.status);

    return (
        <article className="adm-record">
            <ConfirmDialog
                open={cancelOpen}
                title={`Cancel ${t.code}?`}
                confirmLabel="Cancel trade"
                cancelLabel="Keep it"
                danger
                busy={busy}
                onCancel={() => setCancelOpen(false)}
                onConfirm={cancel}
                message={(
                    <div className="adm-confirm-body">
                        <p>It moves to both traders' history as "Cancelled by TradeSpot staff" and they're both notified. Packages already at a shop still need sorting out with that shop.</p>
                        <textarea rows={2} maxLength={200} placeholder="Reason they will see (optional)"
                            value={reason} onChange={e => setReason(e.target.value)} />
                    </div>
                )}
            />

            <header className="adm-record-head trade">
                <div className="adm-record-title">
                    <h2 className="adm-mono">{t.code || `#${t.tradeId}`}</h2>
                    <div className="adm-chips">
                        <StatusChip status={t.status} />
                        {t.middleman != null && <span className="adm-chip">{t.middleman ? 'middleman' : 'direct'}</span>}
                        {open && t.step && <span className="adm-chip">step {t.step} of 4</span>}
                    </div>
                    <p className="adm-muted">
                        Started {shortDate(t.created_at)} · last activity {ago(lastEvent)}
                        {t.cancel_reason && <> · {t.cancel_reason}</>}
                    </p>
                </div>
            </header>

            <div className="adm-parties">
                {[t.owner, t.other].map((p, i) => p ? (
                    <React.Fragment key={p.id}>
                        {i === 1 && <Icon name="swap" size={20} className="adm-swap" />}
                        <button type="button" className="adm-party" onClick={() => go('lookup', { user: p.id })}>
                            <Avatar user={p} size={36} />
                            <span>{p.username}{p.is_banned && <span className="adm-chip bad">banned</span>}</span>
                        </button>
                    </React.Fragment>
                ) : null)}
            </div>

            <div className="adm-actions">
                {t.owner && <Link className="adm-btn" to={`/messages?to=${t.owner.id}`}><Icon name="chat" size={13} /> Message {t.owner.username}</Link>}
                {t.other && <Link className="adm-btn" to={`/messages?to=${t.other.id}`}><Icon name="chat" size={13} /> Message {t.other.username}</Link>}
                {open && (
                    <button type="button" className="adm-btn danger" onClick={() => { setReason(''); setCancelOpen(true); }}>
                        <Icon name="x" size={13} /> Cancel trade
                    </button>
                )}
            </div>
            {msg && <p className="adm-msg">{msg}</p>}

            <section className="adm-block adm-cards-grid">
                <CardList title={`${t.owner?.username} gives`} cards={t.ownerCards} sub={t.ownerSub || null} />
                <CardList title={`${t.other?.username || 'Nobody yet'} gives`} cards={t.otherCards} sub={t.otherSub || null} />
            </section>

            <section className="adm-block">
                <h3 className="adm-h3">Packages <span>{legs.length}</span></h3>
                {legs.length === 0
                    ? <p className="adm-muted">No packages yet{t.step ? `; still in setup, step ${t.step} of 4` : ''}.</p>
                    : legs.map(l => <LegTrack key={l.id} leg={l} names={names} />)}
            </section>

            <section className="adm-block">
                <h3 className="adm-h3">History <span>{events.length}</span></h3>
                {events.length === 0 ? <p className="adm-muted">No events recorded (older trades predate the log).</p> : (
                    <ol className="adm-timeline">
                        {events.map(e => (
                            <li key={e.id} className={`ev-${e.type}`}>
                                <time>{dateTime(e.created_at)}</time>
                                <span>{e.detail || e.type.replace(/_/g, ' ')}</span>
                            </li>
                        ))}
                    </ol>
                )}
            </section>
        </article>
    );
}

// ---------------- The page ----------------

const EXAMPLES = ['a username', 'an email', 'a user number like #12', 'a trade code'];

function AdminLookup({ params, go, onChanged }) {
    const userId = params.get('user');
    const tradeRef = params.get('trade');
    const [q, setQ] = useState('');
    const [results, setResults] = useState(null);

    useEffect(() => {
        const term = q.trim();
        if (!term) { setResults(null); return undefined; }
        const t = setTimeout(async () => {
            try { setResults(await adminFetch(`/api/admin/lookup?q=${encodeURIComponent(term)}`)); }
            catch { setResults({ users: [], trades: [] }); }
        }, 220);
        return () => clearTimeout(t);
    }, [q]);

    const hasSelection = !!(userId || tradeRef);
    const showResults = results && q.trim();

    return (
        <div className="adm-lookup">
            <label className="adm-search">
                <Icon name="search" size={18} />
                <input
                    autoFocus={!hasSelection}
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    placeholder="Username, email, user # or trade code"
                    aria-label="Look up a person or trade"
                />
                {q && <button type="button" onClick={() => setQ('')} aria-label="Clear"><Icon name="x" size={14} /></button>}
            </label>

            <div className={`adm-split${showResults && hasSelection ? ' two' : ''}`}>
                {showResults && (
                    <div className="adm-results">
                        {results.users.length === 0 && results.trades.length === 0 && (
                            <p className="adm-muted">Nothing matches "{q.trim()}".</p>
                        )}
                        {results.users.length > 0 && (
                            <>
                                <h4>People</h4>
                                {results.users.map(u => (
                                    <button key={u.id} type="button"
                                        className={`adm-result${String(u.id) === userId ? ' active' : ''}`}
                                        onClick={() => go('lookup', { user: u.id })}>
                                        <Avatar user={u} size={32} />
                                        <span className="adm-result-main">
                                            <strong>{u.username}</strong>
                                            <small>{u.email}</small>
                                        </span>
                                        {u.is_banned && <span className="adm-chip bad">banned</span>}
                                        {u.role !== 'user' && <span className={`adm-chip role-${u.role}`}>{u.role}</span>}
                                    </button>
                                ))}
                            </>
                        )}
                        {results.trades.length > 0 && (
                            <>
                                <h4>Trades</h4>
                                {results.trades.map(t => (
                                    <button key={t.tradeId} type="button"
                                        className={`adm-result${tradeRef === t.trade_code ? ' active' : ''}`}
                                        onClick={() => go('lookup', { trade: t.trade_code || t.tradeId })}>
                                        <span className="adm-result-main">
                                            <strong className="adm-mono">{t.trade_code || `#${t.tradeId}`}</strong>
                                            <small>{t.owner_name} and {t.other_name || 'nobody yet'}</small>
                                        </span>
                                        <StatusChip status={t.status} />
                                    </button>
                                ))}
                            </>
                        )}
                    </div>
                )}

                {hasSelection ? (
                    <div className="adm-detail">
                        {tradeRef
                            ? <TradeDetail key={tradeRef} tradeRef={tradeRef} go={go} onChanged={onChanged} />
                            : <UserDetail key={userId} id={userId} go={go} onChanged={onChanged} />}
                    </div>
                ) : !showResults && (
                    <div className="adm-empty">
                        <Icon name="search" size={28} />
                        Find anyone or any trade. Try {EXAMPLES.map((e, i) => (
                            <React.Fragment key={e}>{i > 0 && (i === EXAMPLES.length - 1 ? ' or ' : ', ')}<em>{e}</em></React.Fragment>
                        ))}.
                    </div>
                )}
            </div>
        </div>
    );
}

export default AdminLookup;
